import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import * as React from 'react';

import { useShadowRoot } from './ShadowRootContext';
import { cn } from './cn';

interface DialogContextValue {
  modal: boolean;
  open: boolean;
}

const DialogContext = React.createContext<DialogContextValue>({
  modal: true,
  open: false,
});

const Dialog = ({
  modal = true,
  open,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root>) => {
  const contextValue = React.useMemo(
    () => ({ modal, open: open ?? false }),
    [modal, open],
  );

  return (
    <DialogContext.Provider value={contextValue}>
      <DialogPrimitive.Root
        modal={modal}
        {...(open !== undefined && { open })}
        {...props}
      />
    </DialogContext.Provider>
  );
};

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay> | HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => {
  const { modal, open } = React.useContext(DialogContext);

  const baseClasses = cn(
    'fixed inset-0 z-50 bg-black/30 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
    className,
  );

  // For non-modal dialogs, render a simplified div since Radix's Overlay won't mount
  if (!modal) {
    return (
      <div
        ref={ref as React.Ref<HTMLDivElement>}
        data-state={open ? 'open' : 'closed'}
        className={cn(baseClasses, 'data-[state=closed]:hidden')}
        {...props}
      />
    );
  }

  return (
    <DialogPrimitive.Overlay
      ref={ref as React.Ref<React.ElementRef<typeof DialogPrimitive.Overlay>>}
      className={baseClasses}
      {...props}
    />
  );
});
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    close?: boolean;
    overlay?: boolean;
    usesPortal?: boolean;
  }
>(
  (
    {
      className,
      children,
      close = true,
      onInteractOutside,
      overlay = true,
      usesPortal,
      ...props
    },
    ref,
  ) => {
    const { modal } = React.useContext(DialogContext);
    const { container, fullPageContainer } = useShadowRoot();

    const handleInteractOutside = (event: Event) => {
      if (!modal) {
        event.preventDefault();
      }
    };

    const content = (
      <>
        {overlay && <DialogOverlay />}
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            'fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-bg-primary p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] @sm:rounded-lg @md:w-full',
            className,
          )}
          onInteractOutside={onInteractOutside ?? handleInteractOutside}
          {...props}
        >
          {children}
          {close && (
            <DialogPrimitive.Close className="absolute right-4 top-4 rounded opacity-70 ring-offset-bg-secondary transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-border-accent-quaternary focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-bg-accent-strong-secondary data-[state=open]:text-text-tertiary">
              <X className="size-4" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </>
    );

    if (usesPortal === false) {
      return content;
    }

    const portalTarget = fullPageContainer ?? container;
    return <DialogPortal container={portalTarget}>{content}</DialogPortal>;
  },
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col gap-1.5 text-center @sm:text-left', className)}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse gap-4 @sm:flex-row @sm:justify-end @sm:gap-2',
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-lg font-semibold leading-none tracking-tight',
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-text-tertiary', className)}
    {...props}
  />
));

DialogDescription.displayName = DialogPrimitive.Description.displayName;

const DialogClose = DialogPrimitive.Close;

export {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogClose,
};
